import { Fragment, memo, useCallback, useEffect } from 'react';
import { useFormikContext } from 'formik';
import {
  ResearchRegisterVM,
  ResearchRegisterTableVM,
  BodyResearchRegister,
} from 'practice/view-models/research-register';
import { Typography, useEventCallback } from '@mui/material';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { useNavigate } from 'react-router-dom';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { transformDataset } from 'practice/pages/register/utils';
import { HttpResponseList } from 'shell/models/http-response';
import { TypeRegisterData } from 'shell/models/practice-lists';
import {
  HttpRequestFunction,
  RequestOptionsOverride,
  ResponsePromise,
} from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpBackendError } from 'shell/error/definitions/http-backend-error';

interface TableRegisterProps {
  activeDelete?: boolean;
  activeEdit?: boolean;
  getRegisterList:
    | ((options?: RequestOptionsOverride | undefined) => Promise<Error | HttpBackendError>)
    | HttpRequestFunction<HttpResponseList<TypeRegisterData>, BodyResearchRegister[]>;
  setDeleteAlertModal: (value: AlertModal<BodyResearchRegister>) => void;
  registers: Array<BodyResearchRegister>;
}

const TableRegisterComponent = ({
  activeDelete,
  activeEdit,
  getRegisterList,
  setDeleteAlertModal,
  registers,
}: TableRegisterProps): React.ReactElement => {
  const formContext = useFormikContext<ResearchRegisterVM>();

  const navigate = useNavigate();

  const onChange = useEventCallback(
    (
      key: keyof ResearchRegisterTableVM,
      value: ResearchRegisterTableVM[keyof ResearchRegisterTableVM]
    ) => {
      formContext.setFieldValue('table', {
        ...(formContext.values.table || {}),
        [key]: value,
      });
    }
  );

  const onChanges = useEventCallback((values: ResearchRegisterTableVM) => {
    formContext.setFieldValue('table', {
      ...(formContext.values.table || {}),
      ...values,
    });
  });

  const onChangePage = useEventCallback((page: number) => {
    onChange('page', page || 0);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    onChanges({
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    });
  });

  const onVisibilityPress = useEventCallback(
    (cell: BodyResearchRegister): Promise<BodyResearchRegister> => {
      return new Promise(() => {
        navigate(`/pratiche/ricerca-registro/dettaglio/${cell.type}`);
      });
    }
  );

  const onEditPress = useEventCallback(
    (cell: BodyResearchRegister): Promise<BodyResearchRegister> => {
      return new Promise(() => {
        navigate(`/pratiche/ricerca-registro/modifica/${cell.type}`);
      });
    }
  );

  const onTrashPress = useCallback(
    (cell: BodyResearchRegister): Promise<BodyResearchRegister> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<BodyResearchRegister>;
    },
    [setDeleteAlertModal]
  );

  useEffect(() => {
    if (registers?.length > 0) {
      formContext.setFieldValue('table', {
        ...formContext.values?.table,
        body: registers,
      });
    } else {
      formContext.setFieldValue('table', {
        ...formContext.values?.table,
        body: registers,
      });
    }
  }, [registers]);

  const onSort = useCallback(
    (orders) => {
      const newDataset = {
        ...formContext.values,
        filter: {
          ...formContext.values?.filter,
          orders,
        },
      };
      const queryString = transformDataset(newDataset);
      (
        getRegisterList({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<TypeRegisterData>, Array<BodyResearchRegister>>
      ).then(() => {
        formContext.setFieldValue('filter', {
          ...{
            ...formContext.values?.filter,
            orders,
          },
        });
      });
    },
    [formContext.setFieldValue, getRegisterList, formContext.values]
  );

  return (
    <Fragment>
      {formContext.values.table?.body ? (
        (formContext.values.table?.body || [])?.length > 0 ? (
          <Table
            ariaLabel="Lista registri"
            page={formContext.values.table?.page || 0}
            rowsPerPage={formContext.values.table?.rowsPerPage}
            body={formContext.values.table?.body || []}
            count={(formContext.values.table?.body || [])?.length}
            head={headCells}
            onSort={onSort}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            orders={formContext.values.filter?.orders}
            isNotLayoutFixed
            activeActions={{
              delete: activeDelete,
              read: true,
              edit: activeEdit,
            }}
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

TableRegisterComponent.displayName = 'Table Registe component';

const TableRegister = memo(TableRegisterComponent);

export default TableRegister;
